import { Component, OnInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe, NgTemplateOutlet } from '@angular/common';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { forkJoin } from 'rxjs';
import Swal from 'sweetalert2';

import { MAT_FORM_FIELD_DEFAULT_OPTIONS, MatFormFieldDefaultOptions, MatFormFieldModule } from '@angular/material/form-field';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { RouterLink } from '@angular/router';

import { MenuCommonComponent } from '@app/shared/menu-common/menu-common.component';
import { HrService, hrErrorMessage } from '../hr.service';
import { CommonService } from '@app/common/common.service';
import { ValuesService } from '@app/common/values/values.service';
import { LoginService } from '@app/common/login.service';
import { AlertService } from '@app/shared/alert.service';
import { HrActivity, HrEmployee, HrRequirement, HrSchedule, HrTeam, hrActivityColor, hrEmployeeNameOf, hrHoursData, hrIsHrManager, hrOpeningOf, hrResolveActivities } from '@app/shared/_models/hr';
import { C_value, Store, User } from '@app/shared/_models/common';
import {
	C_VALUES_HR_ABSENCE_TYPE, C_VALUES_HR_HOURS_TEMPLATES, C_VALUES_HR_PAYROLL_RULES,
	C_VALUES_HR_TEAMS, C_VALUES_USER_ROLE, EVENTTYP_DELETE, EVENTTYP_INSERT, EVENTTYP_UPDATE,
	HR_PERSON_COLORS, OBJECTTYP_HR_ACTIVITY, isSystemValue,
} from '@app/shared/constants';
import { minutesLabel, minutesOfHhmm } from '../hr-schedule-engine';
import { nowIso, todayStr } from '@app/shared/time';
import { HrTimeSource, PayrollRules, hrPayrollRulesOf } from '../hr-payroll';
import {
	MEAL_EMPLOYER_SHARE_MAX, MEAL_EMPLOYER_SHARE_MIN, MealVoucherWarning,
	euros, mealVoucherRulesOf, mealVoucherSharesOf, mealVoucherWarnings,
} from '../hr-meal-voucher';
import {
	HR_LEGAL, LeaveValuation, OvertimeDestination, OvertimeLeaveBasis, OvertimeTierKind,
	PremiumKind, PremiumRule, RcrDecider, corRateForStaff,
} from '../hr-worktime';
import {
	AbsenceQuota, HR_ABSENCE_CATALOG, hrAbsenceCatalogOf, hrAbsenceProfileLabel, hrAbsenceProfileOf,
} from '../hr-absence-catalog';
import { absenceQuotaOf, quotaDaysLabel, seniorityLabel } from '../hr-absence-quota';
import { LeaveDayUnit, LeaveRules, leaveRulesOf, monthDayLabel, monthDayOf } from '../hr-leave-rights';
import { isPayrollFormError, mealDraftOf, payrollRulesFromForm } from './hr-payroll-form';
import {
	HR_HOLIDAY_MAY_FIRST_MIN_RATE, HR_HOLIDAY_RULES_DEFAULT, HR_HOLIDAY_ZONES, HolidayDay, HolidayRules,
	holidayHorizonOf, holidaysInRange, isMayFirst, mergeHolidays, needsHolidayRefresh,
} from '../hr-holidays';
import { HrHolidaysService } from '../hr-holidays.service';
import { IdccEntry, hrIdccOf, hrIdccSearch, hrLegifranceSearchUrl } from '../hr-idcc-catalog';
import { IdccPreset, PresetAbsenceType, hrIdccPresetOf } from '../hr-idcc-presets';
import { CompanyIdccMatch, HrIdccService } from '../hr-idcc.service';

// Une plage d'ouverture éditée (« 08:30 » → « 12:00 »).
export interface HourRange {
	start: string;
	end: string;
}

// CE QUE L'ENTREPRISE FAIT DE SES HEURES SUPPLÉMENTAIRES — la question telle qu'un gérant se la
// pose, avant toute considération de vocabulaire juridique :
//   'pay'     elles se paient, point. Le cas par défaut, et celui de la loi sans accord.
//   'rest'    elles ne se paient PAS : elles partent toutes en récupération. C'est licite, mais
//             cela suppose un accord (ou, sans délégué syndical, une décision de l'employeur non
//             contestée par le CSE — L3121-37), parce que le repos REMPLACE alors le salaire.
//   'choose'  on arbitre au cas par cas : qui décide, et ce qu'il propose par défaut.
export type OvertimePolicy = 'pay' | 'rest' | 'choose';

// Les panneaux des règles de paie — un seul visible à la fois.
export type PayrollPanel = 'count' | 'overtime' | 'leave' | 'premium' | 'meal' | 'legal';

// La politique que TRADUISENT des règles enregistrées — la lecture inverse de
// `overtimeDestinationRules`, pour que l'écran retrouve le choix qu'on y avait fait.
export function overtimePolicyOf(
	r: { rcr_enabled: boolean; rcr_decider: RcrDecider; rcr_employer_default: OvertimeDestination },
): OvertimePolicy {
	if (!r.rcr_enabled) return 'pay';
	return (r.rcr_decider === 'employer' && r.rcr_employer_default === 'rest') ? 'rest' : 'choose';
}

// … et la traduction dans l'autre sens, celle qui part en base.
export function overtimeDestinationRules(
	policy: OvertimePolicy, decider: RcrDecider, employerDefault: OvertimeDestination,
): { rcr_enabled: boolean; rcr_decider: RcrDecider; rcr_employer_default: OvertimeDestination } {
	if (policy === 'pay') {
		return { rcr_enabled: false, rcr_decider: decider, rcr_employer_default: employerDefault };
	}
	if (policy === 'rest') {
		return { rcr_enabled: true, rcr_decider: 'employer', rcr_employer_default: 'rest' };
	}
	return { rcr_enabled: true, rcr_decider: decider, rcr_employer_default: employerDefault };
}

// Grille de plages éditable — partagée par les services ET les modèles (mêmes gestes partout).
export interface HoursEditable {
	hours: HourRange[][];	// index 0..6 = lundi..dimanche
	hoursDirty: boolean;
	copyFromCtrl: FormControl<number | null>;		// duplication « Copier [jour] vers [jours] »
	copyToCtrl: FormControl<number[] | null>;
}

// Une activité affichée : la ligne d'édition d'un service d'un établissement.
export interface ActivityVM extends HoursEditable {
	activity: HrActivity;
	nameCtrl: FormControl<string | null>;
	legacy: boolean;			// pseudo-activité (id < 0) : héritée d'un serveur non migré, lecture seule
	saving: boolean;
	showHours: boolean;		// éditeur d'horaires d'ouverture déplié
	templateCtrl: FormControl<number | null>;		// « Partir d'un modèle… »
}

// Un modèle d'horaires (référentiel c_values 24) : des plages par jour, applicables aux services.
export interface TemplateVM extends HoursEditable {
	id: number;
	name: string;
	expanded: boolean;
	targetsCtrl: FormControl<number[] | null>;		// services cibles de « Appliquer à… »
	applying: boolean;
}

export interface StoreVM {
	store: Store;
	activities: ActivityVM[];
	newNameCtrl: FormControl<string | null>;
}

// L'ORDRE D'AFFICHAGE du référentiel des rôles, en trois blocs et toujours le même — c'est ce qui
// se lit à l'écran ET ce qui part en base (le `seq` est réécrit dessus, cf. roleElements) :
//
//   1. les rôles POSÉS PAR LE CODE (id < 0). En tête, parce qu'ils ne se rangent pas : ils ne
//      peuvent être ni renommés, ni retirés, ni déplacés à la main. Les voir surgir au milieu
//      d'une liste qu'on vient d'ordonner n'aurait aucun sens.
//   2. les rôles de l'entreprise, dans l'ordre que le gérant leur a donné à la souris.
//   3. les rôles DÉSACTIVÉS, à la fin. Invisibles à l'écran, mais jamais perdus : le payload
//      c_values porte toujours la liste complète (cf. roleElements).
//
// PURE et IDEMPOTENTE (`filter` conserve l'ordre relatif) : la rejouer ne change rien, ce qui
// permet de l'appliquer aussi bien au chargement qu'à chaque écriture.
export function roleDisplayOrder(list: C_value[]): C_value[] {
	const code = list.filter(r => isSystemValue(r.id));
	const maison = list.filter(r => !isSystemValue(r.id));
	return [
		...code,
		...maison.filter(r => r.is_active !== false),
		...maison.filter(r => r.is_active === false),
	];
}

// Établissements & services — /hr/organization.
// Pour chaque établissement : ses services (activités), et pour chaque service les RÔLES admis
// à y travailler (référentiel c_values 20). Le panneau « Rôles de l'entreprise » permet d'ajouter
// ou de désactiver un rôle du référentiel (l'écriture c_values passe par le brief backend-hr2).
@Component({
	selector: 'app-hr-organization',
	standalone: true,
	imports: [MenuCommonComponent, NgTemplateOutlet, DatePipe, RouterLink, ReactiveFormsModule, FormsModule,
		DragDropModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatButtonModule, MatIconModule,
		MatCheckboxModule, MatRadioModule, MatTooltipModule, MatSlideToggleModule, MatAutocompleteModule],
	templateUrl: './organization.component.html',
	styleUrl: './organization.component.scss',
	// Le thème global (`mat.theme(density: -5)`) masque TOUS les `<mat-label>` et laisse les hints
	// à 16 px, qui débordent alors de leur gouttière. Les métriques sont rétablies localement en
	// tête du .scss ; ici on rend la gouttière élastique pour qu'un hint de deux lignes pousse la
	// suite au lieu de la recouvrir. Correctif global = une ligne, hors périmètre (cf. docs/hr.md).
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [{
		provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
		useValue: { subscriptSizing: 'dynamic' } as MatFormFieldDefaultOptions,
	}],
})
export class OrganizationComponent implements OnInit {

	public hrService = inject(HrService);
	private idccService = inject(HrIdccService);
	private commonService = inject(CommonService);
	private valuesService = inject(ValuesService);
	private loginService = inject(LoginService);
	private alertService = inject(AlertService);
	private holidaysService = inject(HrHolidaysService);

	public storeVMs: StoreVM[] = [];
	public roles: C_value[] = [];			// AFFICHÉS : actifs seulement, dans l'ordre de roleDisplayOrder
	public rolesAll: C_value[] = [];	// VÉRITÉ du référentiel, inactifs compris — base de toute écriture
	// Les deux moitiés AFFICHÉES de `roles`, séparées parce qu'elles ne se manipulent pas pareil :
	// les rôles du code sont en tête et fixes, ceux de l'entreprise se rangent à la souris.
	public systemRoles: C_value[] = [];
	public sortableRoles: C_value[] = [];
	public newRoleCtrl = new FormControl<string | null>(null);
	public templates: TemplateVM[] = [];
	public newTemplateCtrl = new FormControl<string | null>(null);
	// Référentiel des types d'absence (c_values 23) — accident du travail, maternité, trajet…
	public absenceTypes: C_value[] = [];

	// Équipes (c_values 28) — groupes transverses de salariés, panneau latéral.
	public teams: HrTeam[] = [];
	public employees: HrEmployee[] = [];
	private users: User[] = [];							// pour nommer les membres d'une équipe
	public newTeamCtrl = new FormControl<string | null>(null);
	public teamOpen: number | null = null;			// id de l'équipe dépliée

	// Règles de paie (c_values 25) — le bloc « Règles de paie », en pleine largeur.
	public payrollRules: PayrollRules = hrPayrollRulesOf(null);
	public leaveRules: LeaveRules = leaveRulesOf(null);

	// Les règles de paie forment une trentaine de réglages : les dérouler d'un seul tenant donnait
	// une colonne de plusieurs écrans de haut, où l'on ne savait plus ni où l'on était, ni où était
	// le bouton d'enregistrement. On les montre donc UN PANNEAU À LA FOIS — chacun tient sous les
	// yeux — et l'enregistrement suit dans une barre collante (cf. le .scss).
	public readonly payrollPanels: { key: PayrollPanel; label: string; icon: string }[] = [
		{ key: 'count', label: 'Décompte & convention', icon: 'rule' },
		{ key: 'overtime', label: 'Heures supplémentaires', icon: 'more_time' },
		{ key: 'leave', label: 'Congés & jours fériés', icon: 'beach_access' },
		{ key: 'premium', label: 'Majorations', icon: 'trending_up' },
		{ key: 'meal', label: 'Titres-restaurant', icon: 'restaurant' },
		{ key: 'legal', label: 'Ce que la loi impose', icon: 'gavel' },
	];
	public payrollPanel: PayrollPanel = 'count';
	// ---- Jours fériés : calendrier officiel + décision d'ouverture, jour par jour ----
	public holidayRules: HolidayRules = HR_HOLIDAY_RULES_DEFAULT;
	public holidayDays: HolidayDay[] = [];			// ceux de l'horizon, à l'écran
	public holidayZones = HR_HOLIDAY_ZONES;
	public holidayRefreshing = false;
	public holidayDirty = false;
	// Une seule tentative de rafraîchissement automatique par visite de l'écran (cf. refreshHolidays).
	private holidaysRefreshed = false;
	// Les paliers d'heures supplémentaires, en HEURES pour la saisie. `kind` dit ce que la loi fait
	// des heures du palier : majorées, compensées en RTT, récupérées, ou déjà rémunérées.
	public overtimeTiers: {
		from_hours: number; to_hours: number | null; rate: number; kind: OvertimeTierKind; label: string;
	}[] = [];

	// Les quatre régimes proposés à la saisie, dans l'ordre où on les rencontre.
	public readonly tierKinds: { value: OvertimeTierKind; label: string; hint: string }[] = [
		{ value: 'paid', label: 'Majorées', hint: 'Heures supplémentaires payées avec majoration — le cas général (minimum 10 %)' },
		{ value: 'rtt', label: 'Compensées en RTT', hint: 'Converties en jours de repos : aucune majoration, hors contingent' },
		{ value: 'recovery', label: 'Récupération', hint: 'Rattrapage d\'heures perdues (intempéries, pont) : taux normal, hors contingent — L3121-50' },
		{ value: 'unpaid', label: 'Non majorées', hint: 'Équivalence ou forfait déjà rémunéré dans le salaire de base — L3121-13' },
	];
	// Paliers du TEMPS PARTIEL, en pourcentage du contrat (10 % du contrat, puis jusqu'au tiers) :
	// les seuils réels dépendent de chaque contrat, ils ne peuvent pas être saisis en heures.
	public partTimeTiers: { to_percent: number; rate: number }[] = [];
	// Toutes les autres majorations : nuit, dimanche, fériés, et celles créées à la main.
	public premiums: PremiumRule[] = [];
	public premiumKinds: { value: PremiumKind; label: string }[] = [
		{ value: 'night', label: 'Plage de nuit' },
		{ value: 'sunday', label: 'Dimanche' },
		{ value: 'holiday', label: 'Jour férié' },
		{ value: 'weekday', label: 'Un jour de la semaine' },
		{ value: 'time_range', label: 'Une plage horaire' },
		{ value: 'daily_over', label: 'Au-delà de N heures dans la journée' },
	];
	public weekdayChoices = [
		{ value: 1, label: 'Lundi' }, { value: 2, label: 'Mardi' }, { value: 3, label: 'Mercredi' },
		{ value: 4, label: 'Jeudi' }, { value: 5, label: 'Vendredi' }, { value: 6, label: 'Samedi' }, { value: 7, label: 'Dimanche' },
	];
	// Le début de l'ANNÉE DE CONGÉS se choisit, il ne se tape pas : « 06-01 » saisi à la main
	// s'écrivait aussi bien « 01-06 », et l'établissement basculait son exercice de congés d'un
	// caractère mal placé. Mois + jour, deux listes, aucun format à connaître.
	public monthChoices = [
		{ value: 1, label: 'janvier' }, { value: 2, label: 'février' }, { value: 3, label: 'mars' },
		{ value: 4, label: 'avril' }, { value: 5, label: 'mai' }, { value: 6, label: 'juin' },
		{ value: 7, label: 'juillet' }, { value: 8, label: 'août' }, { value: 9, label: 'septembre' },
		{ value: 10, label: 'octobre' }, { value: 11, label: 'novembre' }, { value: 12, label: 'décembre' },
	];
	// 1..28 : le 29, le 30 et le 31 n'existent pas tous les ans, et une année de congés qui
	// commencerait le 31 avril n'a pas de date de début. Le 1er du mois couvre tous les usages.
	public monthDayChoices = Array.from({ length: 28 }, (_, i) => i + 1);
	// --- Convention collective ---------------------------------------------------------------
	// Le numéro IDCC ne se devine pas : on le CHERCHE, soit dans le catalogue KALI embarqué
	// (par mots du titre ou par numéro), soit — mieux — dans l'annuaire des entreprises de
	// l'État, qui sait quelle convention l'établissement déclare réellement.
	public idccSearchCtrl = new FormControl<string>('');
	public idccResults: IdccEntry[] = [];
	public idccChosen: IdccEntry | null = null;			// entrée du catalogue correspondant à l'IDCC saisi
	public companyCtrl = new FormControl<string>('');
	public companyResults: CompanyIdccMatch[] = [];
	public companySearching = false;
	public companySearched = false;
	public idccPreset: IdccPreset | null = null;		// écarts conventionnels connus, proposés à l'application
	// Ordre public (N0) : ce qu'aucun réglage de cet écran ne peut dépasser. Affiché en clair,
	// parce que la question « pourquoi ma saisie de 50 h est-elle ramenée à 48 ? » est légitime.
	public legal = HR_LEGAL;

	public holidayForm = new FormGroup({
		zone: new FormControl<string>('metropole'),
		open_default: new FormControl<boolean>(false),
		refresh_days: new FormControl<number>(30),
		horizon_months: new FormControl<number>(12),
	});
	public payrollForm = new FormGroup({
		// LA POLITIQUE DE L'ENTREPRISE sur les heures supplémentaires, en une question. Elle pilote
		// les trois réglages fins du repos compensateur (cf. `applyOvertimePolicy`) : c'est la
		// formulation dans laquelle un gérant reconnaît sa propre situation, là où « rcr_decider »
		// et « rcr_employer_default » demandent de savoir ce qu'est un repos compensateur de
		// remplacement AVANT de pouvoir répondre.
		overtime_policy: new FormControl<OvertimePolicy>('pay'),
		// Qui compte les heures : 'planning' (défaut, sans badgeuse) ou 'clock'.
		time_source: new FormControl<HrTimeSource>('planning'),
		idcc: new FormControl<string>(''),
		idcc_label: new FormControl<string>(''),
		idcc_kali: new FormControl<string>(''),
		idcc_url: new FormControl<string>(''),
		idcc_pdf: new FormControl<string>(''),
		week_legal_hours: new FormControl<number>(35),
		employer_charges_rate: new FormControl<number>(43),
		night_start: new FormControl<string>('21:00'),
		night_end: new FormControl<string>('06:00'),
		night_rate: new FormControl<number>(0),
		sunday_rate: new FormControl<number>(0),
		holiday_rate: new FormControl<number>(0),
		// Temps de travail légal (cf. hr-worktime.ts et audit-legislation-travail-2026-07-24.md)
		week_working_days: new FormControl<number>(5),
		leave_valuation: new FormControl<LeaveValuation>('day_base'),
		contingent_year_h: new FormControl<number>(220),
		cor_rate: new FormControl<number>(50),
		rcr_enabled: new FormControl<boolean>(false),
		rcr_decider: new FormControl<RcrDecider>('employer'),
		rcr_employer_default: new FormControl<OvertimeDestination>('pay'),
		overtime_leave_basis: new FormControl<OvertimeLeaveBasis>('worked_plus_paid_leave'),
		max_week_conventional_hours: new FormControl<number | null>(null),
		max_week_avg12_hours: new FormControl<number>(44),
		complementary_limit_ratio: new FormControl<number>(0.1),
		// Congés payés
		leave_unit: new FormControl<LeaveDayUnit>('ouvrables'),
		// Début de l'ANNÉE DE CONGÉS, en deux listes plutôt qu'en « MM-JJ » : c'est la date qui
		// commande tout le compteur (l'année N, l'année N-1, la campagne de clôture).
		leave_reference_month: new FormControl<number>(6),
		leave_reference_day: new FormControl<number>(1),
		leave_taking_start: new FormControl<string>('05-01'),
		leave_taking_end: new FormControl<string>('10-31'),
		leave_fractionation: new FormControl<boolean>(true),
		leave_waiver_required: new FormControl<boolean>(true),
		leave_waiver_blocking: new FormControl<boolean>(false),
		// Clôture de l'année de congés (N-1 → N)
		leave_closing_enabled: new FormControl<boolean>(true),
		leave_closing_ack: new FormControl<boolean>(true),
		leave_closing_notice: new FormControl<number>(30),
		leave_carryover_default: new FormControl<'carry' | 'lost'>('carry'),
		// Titres-restaurant. `meal_pivot` est saisi en « HH:MM » comme les plages de nuit : une
		// charnière de repas se pense à l'heure, pas en minutes depuis minuit.
		meal_enabled: new FormControl<boolean>(false),
		meal_morning_min: new FormControl<number>(180),
		meal_afternoon_min: new FormControl<number>(180),
		meal_pivot: new FormControl<string>('12:00'),
		meal_face_value: new FormControl<number>(9),
		meal_employer_share: new FormControl<number>(60),
		meal_exoneration_cap: new FormControl<number>(7.26),
	});
	public newAbsenceTypeCtrl = new FormControl<string | null>(null);
	// Responsable RH (habilitation 1). TOUTES les cartes de cet écran sont sous cette seule garde,
	// carte Rôles comprise depuis la scission du 2026-08-03 : le groupe 20 ne contient plus que des
	// savoir-faire, et il a rejoint HR_VALUES_GROUPS côté serveur (common/src/values.rs).
	public isAdmin = false;
	public loaded = false;

	private activities: HrActivity[] = [];
	private schedules: HrSchedule[] = [];

	ngOnInit(): void {
		this.load();
	}

	// `after` s'exécute une fois l'écran RECHARGÉ. C'est ce qui permet d'écrire un référentiel puis
	// de reposer quelque chose par-dessus (le préréglage conventionnel remplit le formulaire de
	// paie après avoir créé ses types d'absence) sans que le rechargement l'efface au passage.
	private load(after?: () => void): void {
		forkJoin({
			stores: this.commonService.getStores(),
			users: this.hrService.getUsers$(),
			activities: this.hrService.getActivities$(),
			schedules: this.hrService.getSchedules$(),
			requirements: this.hrService.getRequirements$(),
			// Référentiels c_values : valuesOf$ porte les deux pièges du forkJoin (flux qui ne
			// complète jamais, échec qui fige l'écran) — cf. hr.service.ts.
			roles: this.hrService.valuesOf$(C_VALUES_USER_ROLE),
			templates: this.hrService.valuesOf$(C_VALUES_HR_HOURS_TEMPLATES),
			absenceTypes: this.hrService.valuesOf$(C_VALUES_HR_ABSENCE_TYPE),
			payrollRules: this.hrService.payrollRules$(),
			teams: this.hrService.valuesOf$(C_VALUES_HR_TEAMS),
			employees: this.hrService.getEmployees$(),
		}).subscribe(({ stores, users, activities, schedules, requirements, roles, templates, absenceTypes, payrollRules, teams, employees }) => {
			this.users = users;
			this.employees = employees.filter(e => e.is_active !== false);
			this.teams = ((teams ?? []) as C_value[])
				.filter(t => t.is_active !== false)
				.map(t => HrTeam.fromValue(t));
			// Les habilitations du token peuvent être vides (JWT émis avant la scission) : la base
			// fait foi.
			this.isAdmin = hrIsHrManager(this.loginService.loggeduser, users);
			// La carte Rôles a REJOINT les référentiels RH le 2026-08-03 : le groupe 20 ne contient
			// plus que des savoir-faire, et décider quels métiers existent dans l'entreprise est une
			// décision RH. Elle suit donc `isAdmin` comme les cartes 23/24/25/28 — miroir de
			// HR_VALUES_GROUPS côté serveur (common/src/values.rs).
			// Champ « Nouveau rôle » : visible mais MORT pour un non-habilité — proposer la saisie
			// puis essuyer un 403 serait pire.
			if (this.isAdmin) this.newRoleCtrl.enable({ emitEvent: false });
			else this.newRoleCtrl.disable({ emitEvent: false });
			// Règles déjà parsées par HrService (le référentiel 25 ne porte qu'UN élément) ; les
			// jours fériés vivent dans `rules.holidays`.
			this.payrollRules = payrollRules.rules;
			this.leaveRules = payrollRules.leave;
			this.holidayRules = payrollRules.rules.holidays;
			this.fillPayrollForm();
			this.fillHolidayForm();
			// Rafraîchissement PÉRIODIQUE du calendrier officiel : silencieux, et seulement si
			// nécessaire (dernier téléchargement trop vieux, ou horizon de 12 mois plus couvert).
			this.refreshHolidays(false);
			this.schedules = schedules;
			this.activities = hrResolveActivities(activities, schedules, requirements as HrRequirement[]);
			// DEUX listes, volontairement : `rolesAll` est la vérité du référentiel (elle sert à
			// écrire), `roles` est ce qu'on affiche. Les confondre effaçait les rôles désactivés à
			// l'écriture suivante et faisait redescendre la numérotation max+1 (cf. roleElements).
			// Le serveur rend déjà la liste triée par `seq` ; roleDisplayOrder ne fait que remonter
			// les rôles du code en tête et repousser les désactivés à la fin.
			this.rolesAll = roleDisplayOrder(roles as C_value[]);
			this.splitRoles();
			this.absenceTypes = ((absenceTypes ?? []) as C_value[]).filter(t => t.is_active !== false);
			this.templates = ((templates ?? []) as C_value[])
				.filter(t => t.is_active !== false)
				.map(t => this.toTemplateVM(t));
			this.storeVMs = stores.map(store => ({
				store,
				activities: this.activities
					.filter(a => a.id_store === store.id && a.is_active !== false)
					.map(a => this.toVM(a)),
				newNameCtrl: new FormControl<string | null>(null),
			}));
			this.loaded = true;
			after?.();
		});
	}

	public dayLabels = ['Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi', 'Dimanche'];

	private toVM(a: HrActivity): ActivityVM {
		return {
			activity: a,
			nameCtrl: new FormControl<string | null>(a.name),
			legacy: a.id < 0,
			saving: false,
			showHours: false,
			hours: this.hoursGridOf(a),
			hoursDirty: false,
			copyFromCtrl: new FormControl<number | null>(1),
			copyToCtrl: new FormControl<number[]>([]),
			templateCtrl: new FormControl<number | null>(null),
		};
	}

	private hoursGridOf(a: HrActivity): HourRange[][] {
		return [1, 2, 3, 4, 5, 6, 7].map(d => hrOpeningOf(a, d).map(r => ({ start: minutesLabel(r.start_min), end: minutesLabel(r.end_min) })));
	}

	private toTemplateVM(t: C_value): TemplateVM {
		return {
			id: t.id!,
			name: t.name ?? ('Modèle #' + t.id),
			hours: this.hoursGridOf({ data: t.data } as HrActivity),
			hoursDirty: false,
			copyFromCtrl: new FormControl<number | null>(1),
			copyToCtrl: new FormControl<number[]>([]),
			expanded: false,
			targetsCtrl: new FormControl<number[]>([]),
			applying: false,
		};
	}

	// Tous les services réels (cibles possibles d'un modèle), libellés « Établissement — Service ».
	get applyTargets(): { id: number; label: string }[] {
		return this.storeVMs.flatMap(svm => svm.activities
			.filter(vm => !vm.legacy)
			.map(vm => ({ id: vm.activity.id, label: svm.store.name + ' — ' + vm.activity.name })));
	}

	// Résumé compact des horaires d'ouverture (« Lun 08:30–12:00 · 14:00–19:00, +4 j »).
	hoursSummary(vm: ActivityVM): string {
		const days = vm.hours.map((ranges, i) => ({ i, ranges })).filter(d => d.ranges.length > 0);
		if (!days.length) return 'Horaires d\'ouverture non définis';
		const first = days[0];
		const label = first.ranges.map(r => r.start + '–' + r.end).join(' · ');
		return this.dayLabels[first.i].slice(0, 3) + ' ' + label + (days.length > 1 ? (', +' + (days.length - 1) + ' j') : '');
	}

	roleChecked(vm: ActivityVM, roleId: number): boolean {
		return (vm.activity.roles ?? []).includes(roleId);
	}

	roleNames(vm: ActivityVM): string {
		if (!vm.activity.roles?.length) return 'Tous les rôles';
		return this.roles.filter(r => vm.activity.roles.includes(r.id!)).map(r => r.name).join(', ');
	}

	// ============================================================
	// Activités (services) — chaque action est persistée immédiatement
	// ============================================================

	onToggleRole(vm: ActivityVM, roleId: number): void {
		const roles = new Set(vm.activity.roles ?? []);
		if (roles.has(roleId)) roles.delete(roleId); else roles.add(roleId);
		this.saveActivity(vm, { ...vm.activity, roles: Array.from(roles).sort((a, b) => a - b) } as HrActivity);
	}

	onRenameActivity(vm: ActivityVM): void {
		const name = (vm.nameCtrl.value ?? '').trim();
		if (!name || name === vm.activity.name) { vm.nameCtrl.setValue(vm.activity.name); return; }
		this.saveActivity(vm, { ...vm.activity, name } as HrActivity);
	}

	// Écriture FLUIDE : la ligne se met à jour sur place (pas de rechargement complet de la page) ;
	// seule une pseudo-activité héritée recharge tout (sa création change les ids partout).
	private saveActivity(vm: ActivityVM, next: HrActivity): void {
		vm.saving = true;
		const wasLegacy = next.id < 0;
		const typ = next.id > 0 ? EVENTTYP_UPDATE : EVENTTYP_INSERT;		// pseudo-activité ⇒ création réelle
		this.hrService.postHrEvent(typ, OBJECTTYP_HR_ACTIVITY, this.hrService.buildActivityPayload(next)).subscribe({
			next: (res) => {
				if (wasLegacy) { this.load(); return; }
				Object.assign(vm.activity, next, res?.data?.id ? { id: +res.data.id } : {});
				vm.nameCtrl.setValue(vm.activity.name);
				vm.saving = false;
			},
			error: (e) => { vm.saving = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// ============================================================
	// Horaires d'ouverture du service (hr_activity.data.hours — aucun schéma serveur à toucher)
	// ============================================================

	onAddHourRange(vm: HoursEditable, dayIdx: number): void {
		const last = vm.hours[dayIdx][vm.hours[dayIdx].length - 1];
		vm.hours[dayIdx].push({ start: last?.end || '08:30', end: '' });
		vm.hoursDirty = true;
	}

	onRemoveHourRange(vm: HoursEditable, dayIdx: number, i: number): void {
		vm.hours[dayIdx].splice(i, 1);
		vm.hoursDirty = true;
	}

	onHoursEdited(vm: HoursEditable): void {
		vm.hoursDirty = true;
	}

	// Valide et convertit une grille de plages en minutes ; null si une plage est invalide (alerte posée).
	private parseHoursGrid(hours: HourRange[][], label: string): { weekday: number; start_min: number; end_min: number }[] | null {
		const out: { weekday: number; start_min: number; end_min: number }[] = [];
		for (let d = 0; d < 7; d++) {
			let prevEnd = -1;
			for (const r of hours[d]) {
				const start = minutesOfHhmm(r.start);
				const end = r.end === '24:00' ? 1440 : minutesOfHhmm(r.end);
				if (start === null || end === null || end <= start) {
					this.alertService.error(label + ' — ' + this.dayLabels[d] + ' : plage incomplète ou fin avant début.');
					return null;
				}
				if (start < prevEnd) {
					this.alertService.error(label + ' — ' + this.dayLabels[d] + ' : les plages se chevauchent.');
					return null;
				}
				prevEnd = end;
				out.push({ weekday: d + 1, start_min: start, end_min: end });
			}
		}
		return out;
	}

	// « Copier [jour source] vers [jours cochés] » — même mécanique que les Effectifs minimum.
	onCopyDays(vm: HoursEditable): void {
		const from = vm.copyFromCtrl.value;
		const targets = vm.copyToCtrl.value ?? [];
		if (from === null || !targets.length) return;
		for (const t of targets) {
			if (t === from || t < 1 || t > 7) continue;
			vm.hours[t - 1] = vm.hours[from - 1].map(r => ({ ...r }));
		}
		vm.copyToCtrl.setValue([]);
		vm.hoursDirty = true;
	}

	// Pré-remplit l'éditeur avec un modèle — on AJUSTE ensuite, rien n'est écrit avant « Enregistrer ».
	onApplyTemplateTo(vm: ActivityVM): void {
		const tpl = this.templates.find(t => t.id === vm.templateCtrl.value);
		if (!tpl) return;
		vm.hours = tpl.hours.map(day => day.map(r => ({ ...r })));
		vm.templateCtrl.setValue(null);
		vm.hoursDirty = true;
	}

	// Sauve les horaires du service comme nouveau modèle réutilisable partout.
	onSaveAsTemplate(vm: ActivityVM): void {
		Swal.fire({
			title: 'Enregistrer comme modèle',
			input: 'text', inputValue: vm.activity.name, inputPlaceholder: 'Nom du modèle (ex : Horaires magasin)',
			showCancelButton: true, confirmButtonText: 'Enregistrer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			const name = (r.value ?? '').toString().trim();
			if (!name) return;
			const maxId = Math.max(0, ...this.templates.map(t => t.id));
			this.templates.push({ ...this.toTemplateVM({ id: maxId + 1, name, data: null } as C_value), hours: vm.hours.map(day => day.map(x => ({ ...x }))) });
			this.postTemplates();
		});
	}

	onSaveHours(vm: ActivityVM): void {
		const ranges = this.parseHoursGrid(vm.hours, vm.activity.name);
		if (ranges === null) return;
		const data = { ...(vm.activity.data ?? {}), hours: hrHoursData(ranges) };
		vm.hoursDirty = false;
		this.saveActivity(vm, { ...vm.activity, data } as HrActivity);
	}

	// ============================================================
	// Modèles d'horaires (référentiel c_values 24) — « on définit une fois,
	// on applique à tous les établissements, puis on ajuste par service »
	// ============================================================

	onAddTemplate(): void {
		const name = (this.newTemplateCtrl.value ?? '').trim();
		if (!name) return;
		const maxId = Math.max(0, ...this.templates.map(t => t.id));
		const vm = this.toTemplateVM({ id: maxId + 1, name, data: null } as C_value);
		vm.expanded = true;
		this.templates.push(vm);
		this.newTemplateCtrl.setValue(null);
		this.postTemplates();
	}

	onSaveTemplate(t: TemplateVM): void {
		if (this.parseHoursGrid(t.hours, t.name) === null) return;
		t.hoursDirty = false;
		this.postTemplates();
	}

	onDeleteTemplate(t: TemplateVM): void {
		Swal.fire({
			title: 'Supprimer le modèle « ' + t.name + ' » ?',
			text: 'Les services qui l\'ont reçu gardent leurs horaires (le modèle n\'est qu\'un tampon).',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.templates = this.templates.filter(x => x !== t);
			this.postTemplates();
		});
	}

	// Applique le modèle à TOUS les services cochés (écrit leurs data.hours), puis recharge.
	onApplyTemplateEverywhere(t: TemplateVM): void {
		const targets = t.targetsCtrl.value ?? [];
		const ranges = this.parseHoursGrid(t.hours, t.name);
		if (ranges === null || !targets.length) return;
		const hours = hrHoursData(ranges);
		const posts = targets
			.map(id => this.activities.find(a => a.id === id))
			.filter((a): a is HrActivity => !!a)
			.map(a => this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_ACTIVITY,
				this.hrService.buildActivityPayload({ ...a, data: { ...(a.data ?? {}), hours } } as HrActivity)));
		if (!posts.length) return;
		t.applying = true;
		forkJoin(posts).subscribe({
			next: () => { t.applying = false; this.alertService.success('Horaires appliqués à ' + posts.length + ' service(s)'); this.load(); },
			error: (e) => { t.applying = false; this.alertService.error(hrErrorMessage(e)); },
		});
	}

	// Sérialise TOUS les modèles vers le référentiel (liste complète remplacée — upsert du groupe).
	private postTemplates(): void {
		const elements: any[] = [];
		for (let i = 0; i < this.templates.length; i++) {
			const t = this.templates[i];
			const ranges = this.parseHoursGrid(t.hours, t.name);
			if (ranges === null) return;
			elements.push({ id: t.id, seq: i + 1, name: t.name, is_active: true, data: { hours: hrHoursData(ranges) } });
		}
		this.hrService.postValuesElements(C_VALUES_HR_HOURS_TEMPLATES, elements, 'Modèles d\'horaires').subscribe({
			next: () => this.alertService.success('Modèles d\'horaires enregistrés'),
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onAddActivity(storeVM: StoreVM): void {
		const name = (storeVM.newNameCtrl.value ?? '').trim();
		if (!name) return;
		const a = Object.assign(new HrActivity(), {
			id: 0, id_store: storeVM.store.id, name,
			seq: storeVM.activities.length + 1, roles: [], is_active: true,
		});
		this.hrService.postHrEvent(EVENTTYP_INSERT, OBJECTTYP_HR_ACTIVITY, this.hrService.buildActivityPayload(a)).subscribe({
			next: () => { storeVM.newNameCtrl.setValue(null); this.alertService.success('Service ajouté'); this.load(); },
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	onDeleteActivity(vm: ActivityVM): void {
		const used = this.schedules.some(s => s.id_activity === vm.activity.id && s.is_active !== false);
		Swal.fire({
			title: 'Supprimer « ' + vm.activity.name + ' » ?',
			text: used
				? 'Un emploi du temps utilise encore ce service — il deviendra orphelin.'
				: 'Le service sera supprimé de ' + (this.storeVMs.find(s => s.store.id === vm.activity.id_store)?.store.name ?? 'cet établissement') + '.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.hrService.postHrEvent(EVENTTYP_DELETE, OBJECTTYP_HR_ACTIVITY, this.hrService.buildActivityPayload(vm.activity)).subscribe({
				next: () => { this.alertService.success('Service supprimé'); this.load(); },
				error: (e) => this.alertService.error(hrErrorMessage(e)),
			});
		});
	}

	// ============================================================
	// Référentiel des rôles (c_values 20) — liste complète remplacée à chaque écriture
	// ============================================================

	// Le tooltip de la carte — partagé par le bouton « Ajouter » et les corbeilles.
	get rolesCardTooltip(): string {
		return this.isAdmin ? ''
			: 'Référentiel des rôles réservé au responsable RH : le serveur refuserait l\'écriture.';
	}

	// Rôle posé par le CODE (id négatif) : du code le cite nommément — « Livreur » (-1) décide de qui
	// est proposé sur une tournée. On l'AFFICHE, parce qu'il est utile de savoir qu'il existe et de
	// pouvoir l'attribuer aux gens, mais on ne le retire ni ne le renomme : la garde serveur
	// (common/src/values.rs) refuserait, et surtout le module logistique désignerait autre chose.
	isSystemRole(role: C_value): boolean {
		return isSystemValue(role.id);
	}

	// Les deux moitiés affichées, refaites depuis `rolesAll` — la seule source d'ordre.
	private splitRoles(): void {
		this.roles = this.rolesAll.filter(r => r.is_active !== false);
		this.systemRoles = this.roles.filter(r => this.isSystemRole(r));
		this.sortableRoles = this.roles.filter(r => !this.isSystemRole(r));
	}

	// LE PAYLOAD PORTE LA LISTE COMPLÈTE, INACTIFS COMPRIS — et c'est le cœur du correctif du
	// 2026-08-03. L'écran n'affiche que les rôles actifs (`this.roles`), mais l'écriture remplace
	// `val` EN BLOC : construire le payload depuis la liste affichée EFFAÇAIT physiquement tout
	// rôle désactivé auparavant, en contradiction avec le contrat du serveur (« suppression =
	// is_active: false, jamais d'effacement »). Pire, `maxId` redescendait alors et le rôle suivant
	// REPRENAIT un id déjà utilisé : les porteurs de l'ancien rôle héritaient silencieusement du
	// nouveau. `rolesAll` est donc la seule base légitime du payload comme du calcul d'id.
	//
	// LE `seq` EST RÉÉCRIT À CHAQUE ÉCRITURE, sur le rang dans roleDisplayOrder : c'est lui qui
	// porte l'ordre voulu jusqu'à la base (la lecture GET /common/values/{id} trie dessus), et le
	// renuméroter en bloc évite les rangs en double ou les trous qu'un `seq ?? id` laissait
	// traîner. Les ids, eux, ne bougent JAMAIS — ce sont eux que portent les fiches employé et les
	// services, et le serveur refuserait de toute façon qu'un élément du code change de numéro.
	private roleElements(list: C_value[] = this.rolesAll): any[] {
		return roleDisplayOrder(list).map((r, i) => ({
			id: r.id, seq: i + 1, name: r.name,
			is_active: r.is_active !== false, data: r.data ?? {},
		}));
	}

	onAddRole(): void {
		if (!this.isAdmin) return;		// miroir de la garde serveur du groupe 20
		const name = (this.newRoleCtrl.value ?? '').trim();
		if (!name) return;
		// max+1 sur la liste COMPLÈTE, mais sur les seuls POSITIFS : un id retiré ne doit jamais être
		// réattribué (sans quoi les personnes qui le portent encore changent de métier sans que
		// personne ne l'ait demandé), et la zone négative est celle du code — un rôle créé à l'écran
		// n'a rien à y faire. `Math.max(0, …)` garantit donc un premier id à 1.
		const maxId = Math.max(0, ...this.rolesAll.filter(r => !this.isSystemRole(r)).map(r => r.id ?? 0));
		// Le nouveau venu se range EN QUEUE des rôles actifs (roleDisplayOrder le place avant les
		// désactivés) : on le retrouve là où on vient de le taper, pas noyé au milieu de la liste.
		const elements = this.roleElements([...this.rolesAll, { id: maxId + 1, name, is_active: true, data: {} }]);
		this.postRoles(elements, () => this.newRoleCtrl.setValue(null));
	}

	onRemoveRole(role: C_value): void {
		if (!this.isAdmin) return;		// miroir de la garde serveur du groupe 20
		if (this.isSystemRole(role)) {
			this.alertService.error('« ' + role.name + ' » est posé par le code : le module logistique le cite nommément pour désigner les livreurs. Il ne peut être ni retiré ni renommé.');
			return;
		}
		const usedBy = this.activities.filter(a => (a.roles ?? []).includes(role.id!)).map(a => a.name);
		Swal.fire({
			title: 'Retirer le rôle « ' + role.name + ' » ?',
			text: usedBy.length ? ('Utilisé par : ' + usedBy.join(', ') + '. Il sera désactivé, pas effacé.') : 'Il sera désactivé, pas effacé.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			// La désactivation est posée AVANT le rangement : le rôle retiré rejoint ainsi le bloc
			// de queue (roleDisplayOrder), et les rangs des rôles qui restent se resserrent.
			const elements = this.roleElements(
				this.rolesAll.map(x => (x.id === role.id ? { ...x, is_active: false } : x)));
			this.postRoles(elements);
		});
	}

	// ============================================================
	// L'ORDRE DE LA LISTE — ranger ses métiers à la main
	// ============================================================
	//
	// Même geste que dans le planning et les horaires types : on prend une ligne par sa poignée,
	// on la monte ou on la descend, elle reste où on l'a mise. L'ordre d'arrivée des rôles ne dit
	// rien de l'entreprise ; celui du terrain, si — et il vaut PARTOUT, puisque le référentiel est
	// commun (fiches employé, services, filtres). C'est bien pour cela qu'il s'enregistre.
	//
	// SEULS LES RÔLES DE L'ENTREPRISE se rangent : ceux posés par le code tiennent la tête de liste
	// et ne sont pas des cibles de dépôt — d'où deux listes distinctes dans le gabarit plutôt qu'un
	// prédicat sur une liste unique, qui aurait laissé la ligne « Livreur » s'écarter au survol
	// pour finalement refuser le dépôt.
	onRoleDrop(event: CdkDragDrop<C_value[]>): void {
		const { previousIndex, currentIndex } = event;
		if (previousIndex === currentIndex || !this.isAdmin) return;		// miroir de la garde serveur du groupe 20

		const before = this.rolesAll;
		const ranges = this.sortableRoles.slice();
		moveItemInArray(ranges, previousIndex, currentIndex);
		// La liste complète se reconstruit autour du déplacement : le code en tête, l'ordre voulu,
		// puis les désactivés — qui ne s'affichent pas mais doivent repartir dans le payload.
		this.rolesAll = roleDisplayOrder([
			...this.rolesAll.filter(r => this.isSystemRole(r)),
			...ranges,
			...this.rolesAll.filter(r => !this.isSystemRole(r) && r.is_active === false),
		]);
		// La ligne se pose TOUT DE SUITE à sa place : l'aller-retour serveur ne doit pas se voir.
		this.splitRoles();
		this.saveRoleOrder(before);
	}

	// Enregistrement du seul ordre : silencieux (une confirmation à chaque glissé serait du bruit),
	// mais un refus REMET L'ÉCRAN DANS L'ÉTAT DE LA BASE — une ligne qui reste où on l'a lâchée
	// alors que rien n'a été enregistré est un mensonge qu'on ne découvrirait qu'au prochain
	// chargement. Pas de `load()` ici, à la différence des autres écritures du référentiel :
	// recharger la page entière ferait clignoter tout l'écran pour un rang qui a changé.
	private saveRoleOrder(before: C_value[]): void {
		this.hrService.postValuesElements(C_VALUES_USER_ROLE, this.roleElements()).subscribe({
			next: () => this.valuesService.updateData(),		// le cache partagé sert l'ordre aux autres écrans
			error: (e) => {
				this.rolesAll = before;
				this.splitRoles();
				this.alertService.error(hrErrorMessage(e));
			},
		});
	}

	private postRoles(elements: any[], after?: () => void): void {
		this.hrService.postValuesElements(C_VALUES_USER_ROLE, elements).subscribe({
			next: () => {
				after?.();
				this.valuesService.updateData();		// rafraîchit le cache partagé (autres écrans abonnés)
				this.alertService.success('Rôles mis à jour');
				this.load();
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// ============================================================
	// Référentiel des types d'absence (c_values 23) — même mécanique que les rôles
	// ============================================================

	// Couleur de pastille d'un type (data.color), grise par défaut.
	absenceTypeColor(t: C_value): string {
		return t.data?.color ?? '#9e9e9e';
	}

	// Un type « constaté » (accident, maladie, maternité) se saisit directement approuvé sur la
	// fiche employé ; un type « demandé » (CP, RTT) passe par le workflow de validation.
	absenceTypeKind(t: C_value): string {
		return t.data?.requires_approval === false ? 'constaté' : 'sur demande';
	}

	// ------------------------------------------------------------
	// Régime légal d'un type d'absence (hr-absence-catalog.ts)
	// ------------------------------------------------------------

	// Le catalogue proposé à la saisie, alphabétique — 35 absences du secteur privé.
	public readonly absenceCatalog = [...HR_ABSENCE_CATALOG].sort((a, b) => a.label.localeCompare(b.label, 'fr'));

	// Le code RETENU pour ce type : celui qui est stocké, sinon celui que son nom laisse deviner.
	// C'est exactement ce que fait le moteur de paie — l'écran ne montre rien d'autre que la réalité.
	absenceTypeCode(t: C_value): string {
		return hrAbsenceProfileOf(t).profile.code;
	}

	// Le type est-il RECONNU, ou son traitement de paie n'est-il qu'une hypothèse ?
	absenceTypeRecognized(t: C_value): boolean {
		return hrAbsenceProfileOf(t).recognized;
	}

	// Ce que la loi fait de ce type, en une phrase — la même que dans le rapport de paie, et avec
	// le rythme d'acquisition RÉELLEMENT en vigueur ici (2,5 j/mois en jours ouvrables, 2,08 en
	// jours ouvrés) : la phrase est juste sous le sélecteur qui règle cette unité.
	absenceTypeLegal(t: C_value): string {
		return hrAbsenceProfileLabel(hrAbsenceProfileOf(t).profile, this.leaveRules);
	}

	// Rattachement explicite à une absence du catalogue. On stocke le CODE, jamais le régime :
	// si la loi change, tous les établissements en profitent sans ressaisie.
	onAbsenceTypeCode(type: C_value, code: string): void {
		const elements = this.absenceTypes.map(t => ({
			...this.absenceTypeElement(t),
			data: t.id === type.id ? { ...(t.data ?? {}), code } : (t.data ?? {}),
		}));
		this.postAbsenceTypes(elements);
	}

	// Le type qui DÉBITE le compteur de récupération (`data.rcr`). C'est lui qui transforme un
	// stock d'heures supplémentaires en congé posable — sans lui, le stock ne se vide jamais.
	isRcrAbsenceType(t: C_value): boolean {
		return t.data?.rcr === true;
	}

	get hasRcrAbsenceType(): boolean {
		return this.absenceTypes.some(t => this.isRcrAbsenceType(t));
	}

	// … et celui qui débite la CONTREPARTIE OBLIGATOIRE EN REPOS (`data.cor`). Un type distinct
	// parce que le droit l'est : la contrepartie se prend dans les deux mois (D3121-19), là où le
	// repos compensateur de remplacement n'a pas de délai propre. Sans type dédié, le compteur de
	// contrepartie ne se solderait jamais et l'alerte des 7 h resterait allumée pour toujours.
	isCorAbsenceType(t: C_value): boolean {
		return t.data?.cor === true;
	}

	// ------------------------------------------------------------
	// Plafond annuel d'un type d'absence (hr-absence-quota.ts)
	// ------------------------------------------------------------
	//
	// « Un jour par salarié et par année civile » : le droit s'épuise, et il faut donc un compteur.
	// La valeur affichée est celle qui S'APPLIQUE — le réglage de l'établissement s'il existe,
	// sinon celui que la convention attache au profil du catalogue. Vider le champ retire le
	// plafond : l'absence redevient un congé ordinaire, sans compteur.

	absenceQuotaOf = absenceQuotaOf;

	absenceQuotaDays(t: C_value): number | null {
		return absenceQuotaOf(t)?.days ?? null;
	}

	absenceQuotaSeniority(t: C_value): number | null {
		const q = absenceQuotaOf(t);
		return q && q.seniority_months > 0 ? q.seniority_months : null;
	}

	absenceQuotaPeriod(t: C_value): 'civil' | 'reference' {
		return absenceQuotaOf(t)?.period ?? 'civil';
	}

	// La phrase qu'un gérant relit pour vérifier ce qu'il vient de saisir.
	absenceQuotaLabel(t: C_value): string {
		const q = absenceQuotaOf(t);
		if (!q) return 'Sans plafond';
		const period = q.period === 'reference' ? 'par période de référence' : 'par année civile';
		const seniority = q.seniority_months > 0 ? ', dès ' + seniorityLabel(q.seniority_months) + ' d\'ancienneté' : '';
		return quotaDaysLabel(q.days) + ' ' + period + seniority + ' — le reliquat est perdu';
	}

	// Une saisie sur l'un des trois champs réécrit le quota entier : c'est un objet, pas trois
	// réglages indépendants, et le laisser à moitié rempli en base ne voudrait rien dire.
	onAbsenceQuota(type: C_value, field: 'days' | 'period' | 'seniority_months', raw: any): void {
		if (!this.isAdmin) return;
		const current = absenceQuotaOf(type);
		const next: AbsenceQuota = {
			days: current?.days ?? 0,
			period: current?.period ?? 'civil',
			seniority_months: current?.seniority_months ?? 0,
		};
		if (field === 'period') {
			next.period = raw === 'reference' ? 'reference' : 'civil';
		} else {
			const n = raw === '' || raw === null ? 0 : +raw;
			next[field] = isNaN(n) || n < 0 ? 0 : n;
		}
		// Plafond vidé : on écrit `null`, pas rien. Effacer la clé rendrait la main au catalogue,
		// qui reproposerait aussitôt le jour de la convention — le gérant aurait retiré un plafond
		// qui revient tout seul. `null` dit « sans plafond », et cela se relit.
		const elements = this.absenceTypes.map(t => {
			const el = this.absenceTypeElement(t);
			if (t.id !== type.id) return el;
			return { ...el, data: { ...(el.data ?? {}), quota: next.days > 0 ? next : null } };
		});
		this.postAbsenceTypes(elements);
	}

	onToggleAbsenceFlag(type: C_value, flag: 'rcr' | 'cor', checked: boolean): void {
		if (!this.isAdmin) return;
		const elements = this.absenceTypes.map(t => {
			const el = this.absenceTypeElement(t);
			return t.id === type.id
				? { ...el, data: { ...(el.data ?? {}), [flag]: checked } }
				: el;
		});
		this.postAbsenceTypes(elements);
	}

	onAddAbsenceType(): void {
		const name = (this.newAbsenceTypeCtrl.value ?? '').trim();
		if (!name) return;
		const maxId = Math.max(0, ...this.absenceTypes.map(t => t.id ?? 0));
		// Nouveau type = fait constaté par défaut (le cas de la reprise de données), payé.
		const elements = [
			...this.absenceTypes.map(t => this.absenceTypeElement(t)),
			{ id: maxId + 1, seq: maxId + 1, name, is_active: true, data: { color: '#607d8b', paid: true, requires_approval: false } },
		];
		this.postAbsenceTypes(elements, () => this.newAbsenceTypeCtrl.setValue(null));
	}

	onRemoveAbsenceType(type: C_value): void {
		Swal.fire({
			title: 'Retirer le type « ' + type.name + ' » ?',
			text: 'Il sera désactivé, pas effacé : les absences déjà saisies avec ce type restent lisibles.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Retirer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			const elements = this.absenceTypes.map(t => ({
				...this.absenceTypeElement(t),
				is_active: t.id === type.id ? false : (t.is_active !== false),
			}));
			this.postAbsenceTypes(elements);
		});
	}

	private absenceTypeElement(t: C_value): any {
		return { id: t.id, seq: t.seq ?? t.id, name: t.name, is_active: t.is_active !== false, data: t.data ?? {} };
	}

	// `after` court APRÈS le rechargement : ce qu'on y repose survit au load (cf. `load`).
	private postAbsenceTypes(elements: any[], after?: () => void, message = 'Types d\'absence mis à jour'): void {
		this.hrService.postValuesElements(C_VALUES_HR_ABSENCE_TYPE, elements).subscribe({
			next: () => {
				this.valuesService.updateData();
				this.alertService.success(message);
				this.load(after);
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// ============================================================
	// ÉQUIPES (référentiel c_values 28) — L7/O05
	// ============================================================
	//
	// Groupes TRANSVERSES : indépendants des établissements et des services. Une « équipe du
	// samedi » peut mélanger deux magasins sans qu'on touche à l'organisation — c'est un filtre
	// de confort, pas une structure.

	onAddTeam(): void {
		const name = (this.newTeamCtrl.value ?? '').trim();
		if (!name) return;
		const maxId = Math.max(0, ...this.teams.map(t => t.id));
		const elements = [
			...this.teams.map(t => HrTeam.toValue(t)),
			HrTeam.toValue(Object.assign(new HrTeam(), { id: maxId + 1, name, members: [] })),
		];
		this.postTeams(elements, () => this.newTeamCtrl.setValue(null));
	}

	onRemoveTeam(team: HrTeam): void {
		Swal.fire({
			title: 'Supprimer l\'équipe « ' + team.name + ' » ?',
			text: 'Les salariés ne sont pas touchés : une équipe n\'est qu\'un filtre.',
			icon: 'warning', showCancelButton: true, confirmButtonText: 'Supprimer', cancelButtonText: 'Annuler',
		}).then(r => {
			if (!r.isConfirmed) return;
			this.postTeams(this.teams.filter(t => t.id !== team.id).map(t => HrTeam.toValue(t)));
		});
	}

	onToggleTeamMember(team: HrTeam, id_employee: number, checked: boolean): void {
		const members = checked
			? [...team.members, id_employee]
			: team.members.filter(m => m !== id_employee);
		const elements = this.teams.map(t =>
			HrTeam.toValue(t.id === team.id ? Object.assign(new HrTeam(), t, { members }) : t));
		this.postTeams(elements);
	}

	isTeamMember(team: HrTeam, id_employee: number): boolean {
		return team.members.includes(id_employee);
	}

	employeeName(id: number): string {
		return hrEmployeeNameOf(this.employees.find(e => e.id === id), this.users);
	}

	// ============================================================
	// Couleur d'un service — elle identifie le SERVICE en vue Employés
	// ============================================================

	activityColor(vm: ActivityVM): string {
		return hrActivityColor(vm.activity, HR_PERSON_COLORS);
	}

	// Enregistrée immédiatement, comme les rôles admis : un choix de couleur n'a pas à attendre
	// un bouton « Enregistrer » qui n'existe pas sur cette carte.
	onActivityColor(vm: ActivityVM, color: string): void {
		if (vm.legacy || !this.isAdmin) return;
		const previous = vm.activity.data?.color ?? null;
		vm.activity.data = { ...(vm.activity.data ?? {}), color };
		vm.saving = true;

		const payload = this.hrService.buildActivityPayload(vm.activity);
		this.hrService.postHrEvent(EVENTTYP_UPDATE, OBJECTTYP_HR_ACTIVITY, payload).subscribe({
			// Pas de garde success ici : postHrEvent convertit déjà tout refus métier en `error`.
			next: () => { vm.saving = false; },
			error: (e) => {
				// Retour à l'état précédent : la pastille ne doit pas mentir sur ce qui est en base.
				vm.saving = false;
				vm.activity.data = { ...(vm.activity.data ?? {}), color: previous };
				this.alertService.error(hrErrorMessage(e));
			},
		});
	}

	private postTeams(elements: any[], after?: () => void): void {
		this.hrService.postValuesElements(C_VALUES_HR_TEAMS, elements, 'Équipes').subscribe({
			next: () => {
				after?.();
				this.valuesService.updateData();
				this.alertService.success('Équipes mises à jour');
				this.load();
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// ============================================================
	// RÈGLES DE PAIE (référentiel c_values 25 — UN seul élément) — L1.1
	// ============================================================
	//
	// Ce sont les réglages qui font sortir un rapport de paie juste : convention collective,
	// seuil et paliers d'heures supplémentaires, plage de nuit, titres-restaurant.
	// Le groupe est créé par UPSERT à la première sauvegarde (comme les modèles d'horaires).

	// Remplit le formulaire depuis le référentiel chargé.
	private fillPayrollForm(): void {
		const r = this.payrollRules;
		this.payrollForm.patchValue({
			time_source: r.time_source,
			idcc: r.idcc, idcc_label: r.idcc_label,
			idcc_kali: r.idcc_kali, idcc_url: r.idcc_url, idcc_pdf: r.idcc_pdf,
			week_legal_hours: r.week_legal_min / 60,
			employer_charges_rate: r.employer_charges_rate,
			night_start: minutesLabel(r.night_start_min),
			night_end: minutesLabel(r.night_end_min),
			night_rate: r.night_rate,
			sunday_rate: r.sunday_rate,
			holiday_rate: r.holiday_rate,
			meal_enabled: r.meal_vouchers.enabled,
			meal_morning_min: r.meal_vouchers.min_morning_min,
			meal_afternoon_min: r.meal_vouchers.min_afternoon_min,
			meal_pivot: minutesLabel(r.meal_vouchers.pivot_min),
			meal_face_value: r.meal_vouchers.face_value,
			meal_employer_share: Math.round(r.meal_vouchers.employer_share * 100),
			meal_exoneration_cap: r.meal_vouchers.exoneration_cap,
			week_working_days: r.week_working_days,
			leave_valuation: r.leave_valuation,
			contingent_year_h: r.contingent_year_h,
			cor_rate: r.cor_rate,
			overtime_policy: overtimePolicyOf(r),
			rcr_enabled: r.rcr_enabled,
			rcr_decider: r.rcr_decider,
			rcr_employer_default: r.rcr_employer_default,
			overtime_leave_basis: r.overtime_leave_basis,
			max_week_conventional_hours: r.max_week_conventional_min === null ? null : r.max_week_conventional_min / 60,
			max_week_avg12_hours: r.max_week_avg12_min / 60,
			complementary_limit_ratio: r.complementary_limit_ratio,
			leave_unit: this.leaveRules.acquisition_unit,
			leave_reference_month: +this.leaveRules.reference_start.slice(0, 2),
			leave_reference_day: +this.leaveRules.reference_start.slice(3, 5),
			leave_taking_start: this.leaveRules.taking_period_start,
			leave_taking_end: this.leaveRules.taking_period_end,
			leave_fractionation: this.leaveRules.fractionation_enabled,
			leave_waiver_required: this.leaveRules.fractionation_waiver_required,
			leave_waiver_blocking: this.leaveRules.fractionation_waiver_blocking,
			leave_closing_enabled: this.leaveRules.closing_enabled,
			leave_closing_ack: this.leaveRules.closing_employee_ack,
			leave_closing_notice: this.leaveRules.closing_notice_days,
			leave_carryover_default: this.leaveRules.carryover_default,
		}, { emitEvent: false });
		this.overtimeTiers = r.overtime_tiers.map(t => ({
			from_hours: t.from_min / 60,
			to_hours: t.to_min === null ? null : t.to_min / 60,
			rate: t.rate,
			kind: t.kind ?? 'paid',
			label: t.label ?? '',
		}));
		this.partTimeTiers = r.part_time_tiers.map(t => ({ to_percent: Math.round(t.to_ratio * 1000) / 10, rate: t.rate }));
		this.premiums = r.premiums.map(p => ({ ...p }));
		// Le formulaire vient d'être remis au niveau de la base : plus rien n'est en attente. C'est
		// ce qui éteint le témoin « modifications non enregistrées » de la barre d'action.
		this.payrollForm.markAsPristine();
		this.refreshIdcc();
	}

	// ============================================================
	// Titres-restaurant — aperçu à la saisie
	// ============================================================
	//
	// « Valeur 9 €, part patronale 60 % » ne dit rien au gérant. « L'entreprise paie 5,40 €, le
	// salarié 3,60 € retenus sur son bulletin » répond à la question qu'il se pose vraiment. Tout
	// est recalculé depuis le formulaire EN COURS, pas depuis les règles enregistrées : le chiffre
	// bouge pendant qu'on tape, avant même d'avoir cliqué sur Enregistrer.

	// Les règles telles qu'elles sont saisies à l'instant.
	private get mealDraft() {
		return mealDraftOf(this.payrollForm.value as Record<string, any>);
	}

	// ============================================================
	// Année de congés — l'aperçu qui bouge pendant qu'on choisit
	// ============================================================
	//
	// « reference_start = 06-01 » ne dit rien au gérant. « L'année de congés court du 1er juin au
	// 31 mai ; la clôture s'ouvre le 2 mai » répond à la question qu'il se pose vraiment, et lui
	// montre AVANT d'enregistrer ce que sa date va déclencher.

	private get leaveYearStart(): string {
		const v = this.payrollForm.value as any;
		return monthDayOf(v.leave_reference_month, v.leave_reference_day);
	}

	/// « du 1ᵉʳ juin au 31 mai » — les deux bornes de l'exercice de congés.
	get leaveYearLabel(): string {
		const start = this.leaveYearStart;
		// Le dernier jour est la veille du même quantième l'année suivante. Une année sans
		// importance (2027, non bissextile) suffit à le calculer : on n'affiche qu'un jour et un mois.
		const end = new Date(Date.UTC(2027, +start.slice(0, 2) - 1, +start.slice(3, 5)) - 86_400_000);
		const endMd = String(end.getUTCMonth() + 1).padStart(2, '0') + '-' + String(end.getUTCDate()).padStart(2, '0');
		return 'du ' + monthDayLabel(start) + ' au ' + monthDayLabel(endMd);
	}

	/// Le jour où la campagne de clôture s'ouvre, d'après le préavis saisi.
	get leaveClosingOpensLabel(): string {
		const start = this.leaveYearStart;
		const notice = Math.max(0, Math.min(180, Math.round(+(this.payrollForm.value as any).leave_closing_notice || 0)));
		// Ouverture = début de l'année suivante − préavis (la veille du début est le dernier jour).
		const opens = new Date(Date.UTC(2027, +start.slice(0, 2) - 1, +start.slice(3, 5)) - (notice + 1) * 86_400_000);
		const md = String(opens.getUTCMonth() + 1).padStart(2, '0') + '-' + String(opens.getUTCDate()).padStart(2, '0');
		return monthDayLabel(md);
	}

	get mealEmployerAmount(): string { return euros(mealVoucherSharesOf(this.mealDraft).employer); }
	get mealEmployeeAmount(): string { return euros(mealVoucherSharesOf(this.mealDraft).employee); }
	get mealWarnings(): MealVoucherWarning[] { return mealVoucherWarnings(this.mealDraft); }

	// La fourchette d'exonération, affichée telle que la loi la fixe (50 – 60 %).
	readonly mealShareMin = Math.round(MEAL_EMPLOYER_SHARE_MIN * 100);
	readonly mealShareMax = Math.round(MEAL_EMPLOYER_SHARE_MAX * 100);

	// La valeur faciale qui tire le MEILLEUR parti du plafond : celle dont 60 % font exactement le
	// plafond d'exonération. Au-delà, l'employeur paie des cotisations sur son propre avantage.
	get mealOptimalFaceValue(): string {
		const cap = this.mealDraft.exoneration_cap;
		return cap > 0 ? euros(Math.floor(cap / MEAL_EMPLOYER_SHARE_MAX * 100) / 100) : '—';
	}

	// ============================================================
	// Convention collective — recherche dans les bases de l'État
	// ============================================================

	// Frappe dans le champ de recherche : filtrage du catalogue KALI, en mémoire, sans réseau.
	onIdccSearch(query: string): void {
		this.idccResults = (query ?? '').trim().length < 2 ? [] : hrIdccSearch(query, 25);
	}

	// Une convention est retenue : on fige son numéro, son titre officiel et son lien Légifrance.
	// Ces trois valeurs sont STOCKÉES avec les règles — la fiche d'un salarié doit rester lisible
	// sans dépendre de la présence du catalogue.
	onPickIdcc(e: IdccEntry): void {
		this.payrollForm.patchValue({
			idcc: e.idcc, idcc_label: e.short, idcc_kali: e.kali, idcc_url: e.url,
		});
		this.idccSearchCtrl.setValue('', { emitEvent: false });
		this.idccResults = [];
		this.refreshIdcc();
	}

	// Saisie directe du numéro (celui du bulletin de paie) : on complète depuis le catalogue.
	onIdccNumber(): void {
		const e = hrIdccOf(this.payrollForm.get('idcc')?.value ?? '');
		if (e) this.onPickIdcc(e);
		else this.refreshIdcc();		// numéro hors catalogue : le libellé reste à la main du gérant
	}

	onClearIdcc(): void {
		this.payrollForm.patchValue({ idcc: '', idcc_label: '', idcc_kali: '', idcc_url: '', idcc_pdf: '' });
		this.refreshIdcc();
	}

	// Recherche dans l'ANNUAIRE DES ENTREPRISES (API publique de l'État). Déclenchée par un clic,
	// jamais à la frappe : c'est un appel qui sort de l'application, il doit être voulu.
	onSearchCompany(): void {
		const q = (this.companyCtrl.value ?? '').trim();
		if (q.length < 3) return;
		this.companySearching = true;
		this.companySearched = false;
		this.idccService.searchCompany$(q).subscribe(res => {
			this.companyResults = res;
			this.companySearching = false;
			this.companySearched = true;
		});
	}

	// Un IDCC rapporté par l'annuaire : nommé par le catalogue s'il y figure, retenu tel quel sinon.
	onPickCompanyIdcc(idcc: string): void {
		const e = hrIdccOf(idcc);
		if (e) { this.onPickIdcc(e); return; }
		this.payrollForm.patchValue({ idcc, idcc_label: '', idcc_kali: '', idcc_url: '' });
		this.refreshIdcc();
	}

	// Applique les écarts conventionnels connus — sur clic explicite, jamais au choix de l'IDCC.
	//
	// Deux natures d'écart, deux temps : les CHIFFRES remplissent le formulaire et attendent
	// l'enregistrement ; les CONGÉS que la convention crée partent tout de suite au référentiel,
	// comme toute modification des types d'absence sur cet écran. L'ordre compte — écrire le
	// référentiel recharge la page, on repose donc les chiffres APRÈS (cf. `load`).
	onApplyPreset(): void {
		const p = this.idccPreset;
		if (!p) return;
		const missing = this.missingPresetAbsenceTypes(p);
		if (missing.length === 0) {
			this.applyPresetRules(p);
			this.alertService.success('Réglages de la convention appliqués — vérifiez-les puis enregistrez');
			return;
		}
		this.postAbsenceTypes(
			this.absenceTypesWith(missing),
			() => this.applyPresetRules(p),
			'« ' + missing.map(a => a.name).join(' », « ') + ' » ajouté aux types d\'absence'
				+ ' — vérifiez les règles de paie ci-dessous puis enregistrez',
		);
	}

	private applyPresetRules(preset: IdccPreset): void {
		const q = preset.patch;
		this.payrollForm.patchValue({
			...(q.contingent_year_h !== undefined ? { contingent_year_h: q.contingent_year_h } : {}),
			...(q.max_week_conventional_min !== undefined
				? { max_week_conventional_hours: q.max_week_conventional_min === null ? null : q.max_week_conventional_min / 60 } : {}),
			...(q.complementary_limit_ratio !== undefined ? { complementary_limit_ratio: q.complementary_limit_ratio } : {}),
			...(q.rcr_enabled !== undefined ? { rcr_enabled: q.rcr_enabled } : {}),
			...(q.rcr_decider !== undefined ? { rcr_decider: q.rcr_decider } : {}),
		});
	}

	// Les congés que la convention crée n'existent pour personne tant qu'ils ne sont pas dans le
	// référentiel. Ceux qui manquent VRAIMENT : un type déjà rattaché au même profil — par son code
	// ou par son nom — fait qu'on ne touche à rien, y compris s'il a été désactivé exprès.
	private missingPresetAbsenceTypes(preset: IdccPreset): PresetAbsenceType[] {
		if (!this.isAdmin) return [];
		return (preset.absence_types ?? [])
			.filter(a => !this.absenceTypes.some(t => hrAbsenceProfileOf(t).profile.code === a.code));
	}

	private absenceTypesWith(added: PresetAbsenceType[]): any[] {
		let maxId = Math.max(0, ...this.absenceTypes.map(t => t.id ?? 0));
		return [
			...this.absenceTypes.map(t => this.absenceTypeElement(t)),
			...added.map(a => {
				maxId++;
				return {
					id: maxId, seq: maxId, name: a.name, is_active: true,
					data: {
						color: a.color, code: a.code, paid: true, quota: a.quota,
						// Un jour d'enfant malade se DEMANDE, il ne se constate pas.
						requires_approval: true,
						// … et la pièce attendue vient du catalogue, pas d'une saisie : c'est lui qui sait
						// que la convention l'assortit d'un certificat médical. Le rapport de paie signale
						// ensuite les absences de ce type restées sans justificatif.
						requires_proof: hrAbsenceCatalogOf(a.code)?.certificate === true,
					},
				};
			}),
		];
	}

	// Recalcule ce qui dépend de l'IDCC courant : entrée de catalogue et préréglage disponible.
	private refreshIdcc(): void {
		const n = this.payrollForm.get('idcc')?.value ?? '';
		this.idccChosen = hrIdccOf(n);
		this.idccPreset = hrIdccPresetOf(n);
	}

	// Lien de repli quand le numéro n'est pas dans KALI : la recherche Légifrance par IDCC.
	get idccFallbackUrl(): string {
		return hrLegifranceSearchUrl(this.payrollForm.get('idcc')?.value ?? '');
	}

	// Nom d'un numéro rapporté par l'annuaire — vide si KALI ne le connaît pas (convention locale).
	idccLabelOf(idcc: string): string {
		return hrIdccOf(idcc)?.short ?? '';
	}

	// L'état d'une convention, en clair : « étendue » ne veut rien dire pour un gérant.
	idccStateLabel(e: IdccEntry): string {
		if (e.state === 'etendue') return 'Étendue — elle s\'impose à tout le secteur';
		if (e.state === 'vigueur') return 'En vigueur, non étendue';
		return 'Texte clos (abrogé ou remplacé)';
	}

	// ============================================================
	// Politique des heures supplémentaires — payer, récupérer, arbitrer
	// ============================================================

	// Le choix pilote les trois réglages fins ; on ne les écrit que là où la politique les impose,
	// pour qu'un aller-retour « arbitrer → tout payer → arbitrer » ne perde pas la configuration.
	onOvertimePolicy(policy: OvertimePolicy): void {
		if (policy === 'rest') {
			this.payrollForm.patchValue({ rcr_enabled: true, rcr_decider: 'employer', rcr_employer_default: 'rest' });
		} else if (policy === 'pay') {
			this.payrollForm.patchValue({ rcr_enabled: false });
		} else {
			// On sort du « tout payé » : sans repos compensateur en place, il n'y a rien à arbitrer.
			// Et on quitte le « tout récupéré » sans laisser son défaut, qui rendrait les deux
			// options indiscernables au rechargement.
			this.payrollForm.patchValue({ rcr_enabled: true });
			if (this.payrollForm.value.rcr_decider === 'employer'
				&& this.payrollForm.value.rcr_employer_default === 'rest') {
				this.payrollForm.patchValue({ rcr_employer_default: 'pay' });
			}
		}
	}

	get overtimePolicy(): OvertimePolicy {
		return this.payrollForm.value.overtime_policy ?? 'pay';
	}

	// ============================================================
	// Contrepartie obligatoire en repos — le taux découle de l'EFFECTIF
	// ============================================================

	// 50 % jusqu'à 20 salariés, 100 % au-delà (L3121-30). Ce n'est pas un choix de l'employeur :
	// on le calcule sur les fiches actives et on propose de l'appliquer, plutôt que de laisser
	// cocher une case dont personne ne vérifiera jamais qu'elle correspond à la réalité.
	get staffCount(): number {
		return this.employees.length;
	}

	get corRateForStaff(): number {
		return corRateForStaff(this.staffCount);
	}

	get corRateMatchesStaff(): boolean {
		return +(this.payrollForm.value.cor_rate ?? 50) === this.corRateForStaff;
	}

	onApplyCorRate(): void {
		this.payrollForm.patchValue({ cor_rate: this.corRateForStaff });
	}

	// Le décompte des congés TEL QU'IL EST RÉGLÉ, pour que la phrase d'explication ci-dessous cesse
	// d'annoncer un chiffre qu'elle ne commande pas. Elle affirmait « les 2,08 j/mois affichés sur
	// les fiches » à tout le monde, y compris aux établissements restés en jours ouvrables — où les
	// fiches montrent 2,5. Deux écrans se contredisaient, et c'est celui-ci qu'on croyait.
	get leaveUnitLabel(): string {
		return this.leaveRules.acquisition_unit === 'ouvres' ? 'ouvrés' : 'ouvrables';
	}

	get leaveMonthlyLabel(): string {
		return ('' + this.leaveRules.days_per_month).replace('.', ',');
	}

	// « 35 h ÷ 5 jours = 7 h » — le repère que le gérant doit voir en réglant la semaine.
	get dayBaseLabel(): string {
		const hours = +(this.payrollForm.get('week_legal_hours')?.value ?? 35);
		const days = Math.min(7, Math.max(1, +(this.payrollForm.get('week_working_days')?.value ?? 5)));
		const perDay = hours / days;
		const h = Math.floor(perDay);
		const m = Math.round((perDay - h) * 60);
		return h + ' h' + (m ? ' ' + ('' + m).padStart(2, '0') : '');
	}

	// ============================================================
	// JOURS FÉRIÉS — calendrier officiel (Etalab) + décision d'ouverture
	// ============================================================
	//
	// Le calendrier se rafraîchit TOUT SEUL, sans bouton à penser : à l'ouverture de l'écran, si
	// le dernier téléchargement date de plus de `refresh_days` ou si l'horizon de 12 mois n'est
	// plus couvert, on redemande et on enregistre. Les décisions « ouvert / fermé » déjà prises
	// SURVIVENT à la fusion (hr-holidays.mergeHolidays) : seul le calendrier bouge.
	//
	// Réservé aux ADMINISTRATEURS, parce que l'enregistrement passe par un event c_values.

	private fillHolidayForm(): void {
		const h = this.holidayRules;
		this.holidayForm.patchValue({
			zone: h.zone, open_default: h.open_default,
			refresh_days: h.refresh_days, horizon_months: h.horizon_months,
		}, { emitEvent: false });
		const horizon = holidayHorizonOf(todayStr(), h);
		// À l'écran : l'horizon utile. Le passé récent reste stocké (la paie d'hier doit rester
		// explicable) mais n'a rien à faire dans une liste de cases à cocher.
		this.holidayDays = holidaysInRange(h, todayStr().slice(0, 8) + '01', horizon.to).map(d => ({ ...d }));
		this.holidayDirty = false;
	}

	// Rafraîchissement automatique au chargement (silencieux) ou à la demande (bouton).
	private refreshHolidays(manual: boolean): void {
		if (!this.isAdmin || this.holidayRefreshing) return;
		const today = todayStr();
		if (!manual && !needsHolidayRefresh(this.holidayRules, today)) return;
		// UNE SEULE tentative automatique par visite. Sans ce garde-fou, l'enregistrement du
		// calendrier déclenche un rechargement de l'écran, qui retrouve un référentiel encore en
		// cache (donc « périmé »), qui redéclenche un téléchargement… en boucle.
		if (!manual && this.holidaysRefreshed) return;
		this.holidaysRefreshed = true;

		this.holidayRefreshing = true;
		this.holidaysService.fetch$(this.holidayRules, today).subscribe(({ days, source }) => {
			// Les décisions en cours de saisie sont reversées AVANT la fusion : le calendrier officiel
			// arrive quand il arrive, il n'a pas à effacer la majoration que quelqu'un est en train de
			// taper. `mergeHolidays` sait déjà préserver les taux — encore faut-il les lui donner.
			const base = this.holidayDirty ? this.holidayRulesFromScreen() : this.holidayRules;
			this.holidayRules = {
				...base,
				days: mergeHolidays(base, days, today),
				fetched_at: nowIso(),
				source,
			};
			this.holidayRefreshing = false;
			this.fillHolidayForm();
			// On enregistre : sans ça, le calendrier serait retéléchargé à chaque visite.
			this.savePayrollRules(manual
				? (source === 'api' ? 'Jours fériés à jour (source officielle)' : 'Jours fériés à jour (calcul local : API indisponible)')
				: null);
		});
	}

	onRefreshHolidays(): void {
		this.refreshHolidays(true);
	}

	onToggleHolidayOpen(day: HolidayDay, open: boolean): void {
		day.open = open;
		// Ouvrir un 1ᵉʳ mai sans rien dire de sa majoration serait un piège : la loi impose 100 %
		// (L3133-6), on le pose d'office plutôt que de laisser le plancher agir en silence.
		if (open && isMayFirst(day.dat) && day.rate == null) day.rate = HR_HOLIDAY_MAY_FIRST_MIN_RATE;
		this.holidayDirty = true;
	}

	// Majoration et repos d'UN jour férié. Vide ⇒ `null` : le jour suit la règle générale, ce qui
	// n'est pas la même chose que « 0 % ce jour-là » — d'où le champ effaçable plutôt qu'un zéro.
	onHolidayRate(day: HolidayDay, field: 'rate' | 'rest_rate', value: string): void {
		const raw = (value ?? '').trim();
		day[field] = raw === '' ? null : Math.max(0, +raw || 0);
		this.holidayDirty = true;
	}

	// Le plancher du 1ᵉʳ mai s'applique quoi qu'on saisisse : autant le dire à l'écran.
	holidayRateFloor(day: HolidayDay): number | null {
		return isMayFirst(day.dat) ? HR_HOLIDAY_MAY_FIRST_MIN_RATE : null;
	}

	// Ce que la règle générale donnerait à ce jour, en clair — le repère quand le champ est vide.
	get holidayDefaultLabel(): string {
		const p = this.premiums.find(x => x.kind === 'holiday');
		if (!p) return 'aucune majoration';
		const bits: string[] = [];
		if (p.rate > 0) bits.push('+' + p.rate + ' %');
		if ((p.rest_rate ?? 0) > 0) bits.push(p.rest_rate + ' % en récupération');
		return bits.length ? bits.join(' et ') : 'aucune majoration';
	}

	// Libellé de fraîcheur : d'où vient le calendrier et de quand il date.
	get holidaySourceLabel(): string {
		if (!this.holidayRules.fetched_at) return 'Jamais téléchargé';
		const dat = this.holidayRules.fetched_at.slice(0, 10).split('-').reverse().join('/');
		return (this.holidayRules.source === 'api'
			? 'Source officielle (calendrier.api.gouv.fr)'
			: 'Calcul local (API indisponible)') + ' — ' + dat;
	}

	// Le calendrier STOCKÉ, remis au niveau des décisions prises à l'écran. Les jours affichés sont
	// des COPIES (cf. `fillHolidayForm`) et l'horizon n'en montre qu'une partie : on ne remplace que
	// les jours présents à l'écran, le reste du calendrier est conservé tel quel.
	//
	// Les TROIS décisions voyagent, pas seulement l'ouverture : la majoration et la récupération se
	// saisissent ligne par ligne, et ne reporter que `open` les perdait silencieusement — l'écran
	// affichait 50 %, l'enregistrement écrivait le jour d'avant.
	private holidayRulesFromScreen(): HolidayRules {
		const decided = new Map(this.holidayDays.map(d => [d.dat, d]));
		return {
			...this.holidayRules,
			zone: this.holidayForm.value.zone ?? this.holidayRules.zone,
			open_default: !!this.holidayForm.value.open_default,
			refresh_days: Math.max(1, +(this.holidayForm.value.refresh_days ?? 30)),
			horizon_months: Math.max(1, +(this.holidayForm.value.horizon_months ?? 12)),
			days: this.holidayRules.days.map(d => {
				const e = decided.get(d.dat);
				return e ? { ...d, open: e.open, rate: e.rate ?? null, rest_rate: e.rest_rate ?? null } : d;
			}),
		};
	}

	onSaveHolidays(): void {
		this.holidayRules = this.holidayRulesFromScreen();
		this.holidayDirty = false;
		// Le formulaire de paie peut porter des saisies en attente — le panneau « Congés & jours
		// fériés » mêle les deux (décompte des congés d'un côté, calendrier de l'autre). On les
		// emporte, sinon enregistrer le calendrier les effacerait au rechargement.
		if (!this.payrollForm.dirty) {
			this.savePayrollRules('Jours fériés enregistrés');
			return;
		}
		const data = this.payrollFormData();
		if (data) this.savePayrollRules('Jours fériés enregistrés', data);
	}

	// Paliers et majorations vivent dans des TABLEAUX, hors du formulaire : c'est à ces gestes-là
	// de signaler qu'il reste quelque chose à enregistrer (`markAsDirty`), sinon la barre d'action
	// annoncerait « réglages à jour » sur un palier qu'on vient d'ajouter.
	onAddPartTimeTier(): void {
		this.partTimeTiers.push({ to_percent: 33.3, rate: 25 });
		this.payrollForm.markAsDirty();
	}

	onRemovePartTimeTier(i: number): void {
		this.partTimeTiers.splice(i, 1);
		this.payrollForm.markAsDirty();
	}

	onAddPremium(): void {
		this.premiums.push({
			code: 'premium' + (this.premiums.length + 1), label: 'Nouvelle majoration',
			kind: 'weekday', rate: 10, rest_rate: 0, weekday: 6,
		});
		this.payrollForm.markAsDirty();
	}

	onRemovePremium(i: number): void {
		this.premiums.splice(i, 1);
		this.payrollForm.markAsDirty();
	}

	// Aide de saisie des majorations : chaque nature n'a pas les mêmes paramètres.
	premiumNeeds(p: PremiumRule, what: 'range' | 'weekday' | 'over'): boolean {
		if (what === 'range') return p.kind === 'night' || p.kind === 'time_range';
		if (what === 'weekday') return p.kind === 'weekday';
		return p.kind === 'daily_over';
	}

	// Les bornes horaires d'une majoration se saisissent en « HH:MM » et se stockent en minutes.
	premiumTime(p: PremiumRule, field: 'from_min' | 'to_min'): string {
		return minutesLabel(p[field] ?? 0);
	}

	onPremiumTime(p: PremiumRule, field: 'from_min' | 'to_min', value: string): void {
		const min = minutesOfHhmm(value);
		if (min !== null) p[field] = min;
	}

	onAddOvertimeTier(): void {
		const last = this.overtimeTiers[this.overtimeTiers.length - 1];
		const from = last ? (last.to_hours ?? last.from_hours + 8) : 35;
		// Le palier précédent, jusqu'ici illimité, se referme sur le nouveau seuil.
		if (last && last.to_hours === null) last.to_hours = from;
		this.overtimeTiers.push({ from_hours: from, to_hours: null, rate: 50, kind: 'paid', label: '' });
		this.payrollForm.markAsDirty();
	}

	// Passer un palier en RTT / récupération / équivalence remet sa majoration à zéro : ces heures-là
	// ne se majorent pas. Le retour en « majorées » repropose le supplétif plutôt qu'un 0 % illégal.
	onTierKindChange(t: { rate: number; kind: OvertimeTierKind }): void {
		t.rate = t.kind === 'paid' ? Math.max(HR_LEGAL.MIN_OVERTIME_RATE, t.rate || 25) : 0;
		this.payrollForm.markAsDirty();
	}

	onRemoveOvertimeTier(i: number): void {
		this.overtimeTiers.splice(i, 1);
		// Le dernier palier redevient illimité : sinon les heures au-delà ne seraient plus majorées.
		if (this.overtimeTiers.length) this.overtimeTiers[this.overtimeTiers.length - 1].to_hours = null;
		this.payrollForm.markAsDirty();
	}

	onSavePayrollRules(): void {
		const data = this.payrollFormData();
		if (!data) return;
		// Le calendrier peut porter des décisions encore à l'écran, pour la raison symétrique de
		// `onSaveHolidays` : sans ce report, enregistrer les règles effacerait la majoration qu'on
		// vient de saisir sur un férié.
		if (this.holidayDirty) {
			this.holidayRules = this.holidayRulesFromScreen();
			this.holidayDirty = false;
		}
		this.savePayrollRules('Règles de paie enregistrées', data);
	}

	// Les règles TELLES QU'ELLES SONT À L'ÉCRAN, prêtes pour le référentiel. `null` = saisie
	// invalide : le refus est affiché ici, l'appelant n'a plus qu'à renoncer.
	// La TRADUCTION elle-même est pure et testée — cf. organization/hr-payroll-form.ts : c'est
	// elle qui décide de ce qui part en paie, elle ne pouvait pas rester non vérifiable.
	private payrollFormData(): any | null {
		const res = payrollRulesFromForm({
			form: this.payrollForm.value as Record<string, any>,
			overtimeTiers: this.overtimeTiers,
			partTimeTiers: this.partTimeTiers,
			premiums: this.premiums as any,
			overtimeDestinationRules,
		});
		if (isPayrollFormError(res)) {
			this.alertService.error(res.error);
			return null;
		}
		return res.data;
	}

	// Écriture du référentiel 25. Le calendrier des fériés voyage TOUJOURS avec les règles (un
	// seul élément c_values) : enregistrer les majorations ne doit pas effacer les décisions
	// d'ouverture, et inversement. `message` à null = enregistrement silencieux (rafraîchissement
	// automatique du calendrier), `data` absent = on ne touche qu'aux fériés.
	private savePayrollRules(message: string | null, data?: any): void {
		const payload = { ...(data ?? this.payrollRulesData()), holidays: this.holidayRules };
		const elements = [{ id: 1, seq: 1, name: 'Règles de paie', is_active: true, data: payload }];
		this.hrService.postValuesElements(C_VALUES_HR_PAYROLL_RULES, elements, 'Règles de paie').subscribe({
			next: () => {
				this.valuesService.updateData();
				// Enregistrement SILENCIEUX (rafraîchissement automatique du calendrier) : on ne
				// recharge PAS l'écran. Ce qui vient d'être écrit est déjà ce qu'on a en mémoire, et
				// le rechargement remplacerait au passage les saisies en cours par ce qui était en
				// base — la personne perdrait ce qu'elle tape à l'instant où l'API des fériés répond.
				if (!message) return;
				this.alertService.success(message);
				this.load();
			},
			error: (e) => this.alertService.error(hrErrorMessage(e)),
		});
	}

	// Les règles TELLES QU'ELLES SONT EN MÉMOIRE — pour réenregistrer sans passer par le formulaire
	// (cas du rafraîchissement automatique des fériés, où le panneau n'est peut-être même pas ouvert).
	private payrollRulesData(): any {
		const { holidays, ...rest } = this.payrollRules;
		return rest;
	}
}
